// Dialoge der Zeiterfassung im App-Stil (design_handoff_personal Screens 06–08). Sie ersetzen die Formulare
// in der Pille und die window.prompt-Abfragen — gleiche Texte, gleiche Prüfungen, gleiche Folgeaktion.
import { useMemo, useState, type ReactNode } from 'react';
import { CheckCircle, ClockClockwise, ClockCountdown, PaperPlaneTilt, Stack, Trash, UserGear, WarningCircle } from '@phosphor-icons/react';
import { DialogHuelle, FeldRaster, ImmoButton, TextFeld } from '../../../../components/immobilien/ui';
import { GlasAuswahl } from '../../../../components/ui/GlasAuswahl';
import { korrekturBeantragen, mehrarbeitBeantragen, stempelSpeichern, zuordnungSpeichern } from '../../../../lib/zeiterfassung/api';
import { stundenText, tagVon, type Arbeitszeitmodell, type Zuordnung } from '../../../../lib/zeiterfassung/berechnung';
import { WOCHENTAGE, alsEingabe, minutenAusEingabe, sollAusModell, wochenMinuten } from '../../../../lib/zeiterfassung/arbeitszeit';
import { nettoVorschau, pruefeMehrarbeit } from '../../../../lib/personalAnsicht/darstellung';

const LABEL = { fontSize: 12, fontWeight: 700, color: 'var(--ink3)' } as const;
const HINWEIS = { fontSize: 12, lineHeight: 1.5, color: 'var(--ink3)', margin: 0 } as const;

function Fehler({ text }: { text: string }) {
  return (
    <div role="alert" className="flex items-start" style={{ gap: 8, padding: '10px 12px', borderRadius: 14, background: 'var(--bad-soft)', color: 'var(--bad)', fontSize: 13, fontWeight: 700 }}>
      <WarningCircle size={16} weight="fill" className="shrink-0" style={{ marginTop: 1 }} />{text}
    </div>
  );
}

function Fuss({ onAbbrechen, laeuft, gesperrt, onOk, icon, children, tip }: {
  onAbbrechen: () => void; laeuft: boolean; gesperrt?: boolean; onOk: () => void; icon: typeof CheckCircle; children: ReactNode; tip?: string;
}) {
  return (
    <div className="flex w-full justify-end" style={{ gap: 10 }}>
      <ImmoButton groesse={44} onClick={onAbbrechen} disabled={laeuft}>Abbrechen</ImmoButton>
      <ImmoButton art="akzent" groesse={44} icon={icon} onClick={onOk} disabled={laeuft || gesperrt} data-tip={tip}>{children}</ImmoButton>
    </div>
  );
}

function TextBereich({ label, wert, onChange, placeholder, autoFocus }: {
  label: string; wert: string; onChange: (w: string) => void; placeholder?: string; autoFocus?: boolean;
}) {
  return (
    <label className="flex flex-col" style={{ gap: 6 }}>
      <span style={LABEL}>{label}</span>
      <textarea autoFocus={autoFocus} value={wert} onChange={(e) => onChange(e.target.value)} rows={3} placeholder={placeholder}
        style={{ resize: 'vertical', borderRadius: 15, padding: '12px 14px', border: 'none', outline: 'none', background: 'var(--bg)',
          boxShadow: 'inset 0 0 0 1px var(--line)', color: 'var(--ink)', fontSize: 15, fontWeight: 600, fontFamily: 'inherit', lineHeight: 1.45 }} />
    </label>
  );
}

// ─── Zeit nachtragen / korrigieren / Korrektur beantragen (06) ──────────────────

export interface ZeitVorgabe { id?: string; tag: string; von: string; bis: string; pause: number; notiz: string }

/** Ersetzt das ZeitFormular aus der Pille. `direkt` = false: die Eingabe wird zum Korrekturantrag. */
export function ZeitDialog({ titel, mitarbeiterId, vorgabe, direkt, onFertig, onAbbrechen }: {
  titel: string; mitarbeiterId: string; vorgabe?: ZeitVorgabe; direkt: boolean; onFertig: () => void; onAbbrechen: () => void;
}) {
  const [tag, setTag] = useState(vorgabe?.tag || tagVon(new Date().toISOString()));
  const [von, setVon] = useState(vorgabe?.von || '08:00');
  const [bis, setBis] = useState(vorgabe?.bis || '17:00');
  const [pause, setPause] = useState(String(vorgabe?.pause ?? 30));
  const [notiz, setNotiz] = useState(vorgabe?.notiz || '');
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const netto = nettoVorschau(tag, von, bis, pause);

  const speichern = async () => {
    setFehler(null);
    const beginn = new Date(`${tag}T${von}:00`);
    const ende = new Date(`${tag}T${bis}:00`);
    if (Number.isNaN(beginn.getTime()) || Number.isNaN(ende.getTime())) { setFehler('Bitte Datum und Uhrzeiten prüfen.'); return; }
    if (ende <= beginn) { setFehler('Das Ende muss nach dem Beginn liegen.'); return; }
    const pauseMin = Math.max(0, Number(pause) || 0);
    if (pauseMin >= (ende.getTime() - beginn.getTime()) / 60000) { setFehler('Die Pause ist länger als der Zeitraum.'); return; }
    if (!direkt && !notiz.trim()) { setFehler('Bitte kurz begründen, warum die Zeit nachgetragen wird.'); return; }
    setSpeichert(true);
    if (direkt) {
      const ok = await stempelSpeichern({
        id: vorgabe?.id, mitarbeiterId, beginn: beginn.toISOString(), ende: ende.toISOString(),
        notiz, pauseMinuten: pauseMin,
      });
      setSpeichert(false);
      if (!ok) { setFehler('Speichern fehlgeschlagen.'); return; }
    } else {
      const problem = await korrekturBeantragen({
        mitarbeiterId, art: vorgabe?.id ? 'aendern' : 'neu', stempelId: vorgabe?.id || null,
        beginn: beginn.toISOString(), ende: ende.toISOString(), pauseMinuten: pauseMin, begruendung: notiz,
      });
      setSpeichert(false);
      if (problem) { setFehler(problem); return; }
    }
    onFertig();
  };

  return (
    <DialogHuelle titel={titel} icon={ClockClockwise} onSchliessen={onAbbrechen} laeuft={speichert} breite={480}
      fuss={<Fuss onAbbrechen={onAbbrechen} laeuft={speichert} onOk={() => void speichern()} icon={direkt ? CheckCircle : PaperPlaneTilt}
        tip={direkt ? 'Speichern – Zeit direkt ins Stundenkonto übernehmen' : 'Korrektur beantragen – Antrag an die Personalabteilung senden'}>
        {direkt ? 'Speichern' : 'Korrektur beantragen'}</Fuss>}>
      {fehler && <Fehler text={fehler} />}
      <TextFeld label="Tag" type="date" wert={tag} onChange={setTag} kompakt />
      {/* Schmal (Handy) rutscht die Pause in eine eigene Zeile, damit die Uhrzeiten nicht abgeschnitten werden. */}
      <div className="grid" style={{ gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(118px, 1fr))' }}>
        <TextFeld label="Von" type="time" wert={von} onChange={setVon} kompakt />
        <TextFeld label="Bis" type="time" wert={bis} onChange={setBis} kompakt />
        <TextFeld label="Pause (min)" type="number" min={0} step={5} wert={pause} onChange={setPause} kompakt />
      </div>
      <div className="flex items-center justify-between" data-tip="Arbeitszeit netto – Zeitraum minus Pause; ab sechs Stunden mindestens die gesetzliche Pause"
        style={{ padding: '12px 14px', borderRadius: 14, background: 'var(--acc-soft)', color: 'var(--acc)' }}>
        <span style={{ fontSize: 13, fontWeight: 700 }}>Arbeitszeit netto</span>
        <span style={{ fontSize: 18, fontWeight: 800 }}>{netto === null ? '—' : stundenText(netto)}</span>
      </div>
      <TextFeld label={direkt ? 'Notiz' : 'Begründung (Pflicht)'} wert={notiz} onChange={setNotiz} placeholder="z. B. Stempeln vergessen" kompakt />
      <p style={HINWEIS}>
        {direkt
          ? 'Nachgetragene Zeiten werden in der Übersicht gekennzeichnet.'
          : 'Der Antrag geht an die Personalabteilung. Erst nach der Genehmigung zählt die Zeit im Stundenkonto.'}
        {' '}Ist keine Pause angegeben, zieht die Berechnung ab sechs Stunden die gesetzliche Pause ab.
      </p>
    </DialogHuelle>
  );
}

// ─── Arbeitszeit (07) ───────────────────────────────────────────────────────────

/** Ersetzt das ModellFormular aus der Pille (nur Personal/Geschäftsführung). */
export function ArbeitszeitDialog({ mitarbeiterId, modelle, vorhanden, onFertig, onAbbrechen }: {
  mitarbeiterId: string; modelle: Arbeitszeitmodell[]; vorhanden: Zuordnung[]; onFertig: () => void; onAbbrechen: () => void;
}) {
  const letzte = vorhanden[vorhanden.length - 1];
  const [modellId, setModellId] = useState(letzte?.modell_id || modelle[0]?.id || '');
  // Zeiten je Wochentag als Text, damit „5:30" und „5,5" beide tippbar sind. Startwert:
  // die bisherigen persönlichen Zeiten, sonst die des zuletzt gewählten Modells.
  const [zeiten, setZeiten] = useState<Record<string, string>>(() => {
    const start = letzte?.soll_minuten ?? sollAusModell(modelle.find((m) => m.id === letzte?.modell_id) || modelle[0]);
    return Object.fromEntries(WOCHENTAGE.map((t) => [t.schluessel, alsEingabe(Number(start?.[t.schluessel] ?? 0))]));
  });
  const [urlaub, setUrlaub] = useState(letzte?.urlaubstage_jahr != null ? String(letzte.urlaubstage_jahr) : '');
  // Vorgabe: der kommende Monatserste. Rückwirkend zu starten erzeugt Minusstunden
  // für Tage, an denen niemand gestempelt hat (User-Entscheidung 2026-08-12).
  const [gueltigAb, setGueltigAb] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 1, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  });
  const [saldo, setSaldo] = useState(String(letzte?.start_saldo_minuten ?? 0));
  // Abrechnung nach Stunden (#536): kein festes Soll, Wochentage entfallen.
  const [nachStunden, setNachStunden] = useState(() => letzte ? !!letzte.nach_stunden : !!modelle[0]?.nach_stunden);
  const [speichert, setSpeichert] = useState(false);

  // Ein unlesbarer Tag darf nicht still als 0 durchgehen — sonst verschwindet ein
  // Arbeitstag aus dem Soll, ohne dass es jemand merkt.
  const sollMinuten = useMemo(() => {
    const raus: Record<string, number> = {};
    for (const t of WOCHENTAGE) {
      const roh = zeiten[t.schluessel] ?? '';
      const minuten = roh.trim() === '' ? 0 : minutenAusEingabe(roh);
      if (minuten === null) return null;
      raus[t.schluessel] = minuten;
    }
    return raus;
  }, [zeiten]);
  const wocheUngueltig = !nachStunden && sollMinuten === null;
  const wocheMinuten = wochenMinuten(sollMinuten);

  const zuordnen = async () => {
    setSpeichert(true);
    await zuordnungSpeichern({
      mitarbeiterId, modellId, gueltigAb,
      startSaldoMinuten: Math.round(Number(saldo) || 0),
      sollMinuten: nachStunden ? null : sollMinuten,
      urlaubstageJahr: urlaub.trim() === '' ? null : Number(urlaub),
      nachStunden,
    });
    setSpeichert(false);
    onFertig();
  };

  const kachel = { borderRadius: 15, padding: '8px 12px', background: 'var(--bg)', boxShadow: 'inset 0 0 0 1px var(--line)' } as const;

  return (
    <DialogHuelle titel="Arbeitszeit" icon={UserGear} onSchliessen={onAbbrechen} laeuft={speichert} breite={560}
      fuss={<Fuss onAbbrechen={onAbbrechen} laeuft={speichert} gesperrt={!modellId || wocheUngueltig} onOk={() => void zuordnen()} icon={CheckCircle}
        tip="Zuordnen – Arbeitszeit ab dem gewählten Tag für diese Person festlegen">Zuordnen</Fuss>}>
      <div className="flex flex-col" style={{ gap: 6 }}>
        <span style={LABEL}>Vorlage</span>
        <GlasAuswahl wert={modellId} icon={Stack} ariaLabel="Vorlage" tip="Vorlage – füllt die Wochentage; danach ist jeder Tag frei überschreibbar"
          style={{ borderRadius: 15, height: 46 }}
          optionen={modelle.map((m) => ({ wert: m.id, label: m.name }))}
          onChange={(gewaehlt) => {
            setModellId(gewaehlt);
            // Vorlage füllt die Felder — danach ist jeder Tag frei überschreibbar.
            const vorlage = modelle.find((m) => m.id === gewaehlt);
            setNachStunden(!!vorlage?.nach_stunden);
            const soll = sollAusModell(vorlage);
            setZeiten(Object.fromEntries(WOCHENTAGE.map((t) => [t.schluessel, alsEingabe(soll[t.schluessel])])));
          }} />
      </div>

      <label className="flex cursor-pointer items-start" style={{ gap: 10, ...kachel, padding: '10px 12px' }}
        data-tip="Nach Stunden – für Minijob/Aushilfe ohne feste Wochenstunden">
        <input type="checkbox" checked={nachStunden} onChange={(e) => setNachStunden(e.target.checked)}
          style={{ marginTop: 3, width: 16, height: 16, accentColor: 'var(--acc)' }} />
        <span className="flex flex-col" style={{ gap: 2 }}>
          <span style={{ fontSize: 14, fontWeight: 800 }}>Abrechnung nach Stunden</span>
          <span style={{ fontSize: 12, color: 'var(--ink3)', lineHeight: 1.4 }}>
            Kein festes Soll: Bezahlt wird, was gearbeitet wurde – auch nur 3 Stunden im Monat.
            Es entstehen weder Plus- noch Minusstunden, und im Lohnexport stehen alle Stunden als Grundstunden.
          </span>
        </span>
      </label>

      {!nachStunden && <div className="flex flex-col" style={{ gap: 6 }}>
        <span style={LABEL}>Arbeitszeit je Wochentag</span>
        <div className="grid" style={{ gap: 8, gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
          {WOCHENTAGE.map((t) => (
            <label key={t.schluessel} className="flex flex-col" style={{ ...kachel, gap: 2 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink3)' }}>{t.kurz}</span>
              <input type="text" inputMode="decimal" value={zeiten[t.schluessel] ?? '0:00'} placeholder="0:00" aria-label={`Arbeitszeit ${t.lang}`}
                onChange={(e) => setZeiten((v) => ({ ...v, [t.schluessel]: e.target.value }))}
                className="w-full min-w-0"
                style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 16, fontWeight: 800, color: 'var(--ink)', fontFamily: 'inherit', padding: 0 }} />
            </label>
          ))}
          <div className="flex flex-col justify-center" data-tip="Woche – Summe der Wochentage"
            style={{ borderRadius: 15, padding: '8px 12px', gap: 2, background: wocheUngueltig ? 'var(--bad-soft)' : 'var(--acc-soft)' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: wocheUngueltig ? 'var(--bad)' : 'var(--acc)' }}>Woche</span>
            <span style={{ fontSize: 16, fontWeight: 800, color: wocheUngueltig ? 'var(--bad)' : 'var(--acc)' }}>
              {wocheUngueltig ? '—' : stundenText(wocheMinuten)}
            </span>
          </div>
        </div>
        <p style={HINWEIS}>
          Eingabe als „5:30" oder „5,5". Diese Zeiten gelten für die Person und gehen der
          Vorlage vor — für krumme Wochenstunden braucht es also kein eigenes Modell.
        </p>
      </div>}

      <FeldRaster minBreite={140}>
        <TextFeld label="Urlaubstage im Jahr" type="number" min={0} max={60} step={0.5} wert={urlaub} onChange={setUrlaub} kompakt
          placeholder={`${modelle.find((m) => m.id === modellId)?.urlaubstage_jahr ?? 30} (aus der Vorlage)`} />
        <TextFeld label="Gültig ab" type="date" wert={gueltigAb} onChange={setGueltigAb} kompakt />
        <TextFeld label="Startsaldo in Minuten" type="number" step={15} wert={saldo} onChange={setSaldo} kompakt />
      </FeldRaster>
      <p style={HINWEIS}>
        Der Startsaldo ist der Übertrag aus der Zeit vor der App — negativ für Minusstunden.
        Frühere Zuordnungen bleiben erhalten, damit alte Monate richtig gerechnet bleiben.
      </p>
    </DialogHuelle>
  );
}

// ─── Mehrarbeit beantragen (08, ersetzt drei prompt-Fragen) ─────────────────────

export function MehrarbeitDialog({ mitarbeiterId, onFertig, onAbbrechen }: {
  mitarbeiterId: string; onFertig: () => void; onAbbrechen: () => void;
}) {
  const [tag, setTag] = useState(() => tagVon(new Date().toISOString()));
  const [stunden, setStunden] = useState('');
  const [grund, setGrund] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const pruefung = pruefeMehrarbeit(tag, stunden, grund);

  const beantragen = async () => {
    if (!pruefung.ok) return;
    setFehler(null);
    setLaeuft(true);
    const problem = await mehrarbeitBeantragen({ mitarbeiterId, datum: pruefung.datum, minuten: pruefung.minuten, grund: pruefung.grund });
    setLaeuft(false);
    if (problem) { setFehler(problem); return; }
    onFertig();
  };

  return (
    <DialogHuelle titel="Mehrarbeit beantragen" icon={ClockCountdown} onSchliessen={onAbbrechen} laeuft={laeuft} breite={480}
      fuss={<Fuss onAbbrechen={onAbbrechen} laeuft={laeuft} gesperrt={!pruefung.ok} onOk={() => void beantragen()} icon={PaperPlaneTilt}
        tip="Beantragen – Mehrarbeit zur Genehmigung an die Personalabteilung">Beantragen</Fuss>}>
      {fehler && <Fehler text={fehler} />}
      <FeldRaster minBreite={170}>
        <TextFeld label="Für welchen Tag?" type="date" wert={tag} onChange={setTag} kompakt />
        <TextFeld label="Wie viele Stunden?" inputMode="decimal" wert={stunden} onChange={setStunden} placeholder="z. B. 2 oder 1,5" kompakt autoFocus />
      </FeldRaster>
      <TextFeld label="Grund für die Mehrarbeit" wert={grund} onChange={setGrund} placeholder="z. B. Eigentümerversammlung am Abend" kompakt />
    </DialogHuelle>
  );
}

// ─── Löschung beantragen (ersetzt prompt für Mitarbeitende, Entscheidung 3) ─────

export function LoeschAntragDialog({ mitarbeiterId, stempelId, bezeichnung, onFertig, onAbbrechen }: {
  mitarbeiterId: string; stempelId: string; bezeichnung: string; onFertig: () => void; onAbbrechen: () => void;
}) {
  const [grund, setGrund] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const beantragen = async () => {
    if (!grund.trim()) return;
    setFehler(null);
    setLaeuft(true);
    const problem = await korrekturBeantragen({ mitarbeiterId, art: 'loeschen', stempelId, begruendung: grund });
    setLaeuft(false);
    if (problem) { setFehler(problem); return; }
    onFertig();
  };

  return (
    <DialogHuelle titel="Löschung beantragen" untertitel={`Buchung ${bezeichnung}`} icon={Trash} onSchliessen={onAbbrechen} laeuft={laeuft} breite={460}
      iconFarbe={{ farbe: 'var(--bad)', flaeche: 'var(--bad-soft)' }}
      fuss={<Fuss onAbbrechen={onAbbrechen} laeuft={laeuft} gesperrt={!grund.trim()} onOk={() => void beantragen()} icon={PaperPlaneTilt}
        tip="Löschung beantragen – entschieden wird in der Personalabteilung">Löschung beantragen</Fuss>}>
      {fehler && <Fehler text={fehler} />}
      <p className="m-0" style={{ fontSize: 14, lineHeight: 1.45, color: 'var(--ink2)' }}>
        Löschung der Buchung {bezeichnung} beantragen. Entschieden wird in der Personalabteilung.
      </p>
      <TextBereich label="Warum?" wert={grund} onChange={setGrund} autoFocus />
    </DialogHuelle>
  );
}
